import { exitLabel, stagePill } from './messagingLabels';
import { Caption, Card, CardBody, Divider, Label, Row, Title } from './ui';
import Pill from '../../Lifecycle/shared/Pill';
import { formatDateTime, formatDay } from '../../Lifecycle/shared/format';
import { channelLabel, reasonLabel } from '../../Lifecycle/shared/labels';

import Button from '@/components/Button';
import {
  WorkerJourney,
  WorkerLifecycleOverview,
} from '@/services/lifecycleAPI';

// Why graphy sends this worker nothing at all (Suppression.reason_for: a
// declined interview, a suspension, ...). Do not contact keeps its own wording.
// Without this the card promised the next sweep or a next message that the
// send decision will skip (review RV-09). 'paused' is not "nothing at all":
// the overview asks with journey_message: true, so a pause comes back here,
// yet shift messages still go. It keeps the stage pill and the paused wording
// below (bug hunt 16).
const suppressedLabel = (overview: WorkerLifecycleOverview): string | null => {
  const reason = overview.suppressed_reason;
  if (!reason || reason === 'do_not_contact' || reason === 'paused')
    return null;
  return reasonLabel(reason).label;
};

const nextLine = (
  active: WorkerJourney | undefined,
  overview: WorkerLifecycleOverview,
  stepLabel: (key: string) => string,
): string => {
  if (!active) return '';
  const next = active.next_message;
  const suppressed = suppressedLabel(overview);
  if (suppressed) return `Next: nothing (${suppressed}).`;
  if (overview.preferences.journeys_paused_at) {
    return 'Next: nothing while journeys are paused.';
  }
  if (active.holdout) {
    return `Next: nothing. Holdout group gets no ${active.journey_key} messages.`;
  }
  if (!next) return 'Next: nothing planned.';
  // A STOP from the phone number (Shift Blast, carrier, before this engine)
  // blocks the SMS as a worker opt-out does, as the Consent card says (RT-10).
  const optedOut =
    next.channel_planned === 'sms' &&
    (overview.preferences.sms_opted_out_at ||
      overview.preferences.sms_phone_opt_out);
  return `Next: ${channelLabel(next.channel_planned)} ${stepLabel(next.step_key)} on ${formatDateTime(next.scheduled_for)}${optedOut ? ' (SMS opted out)' : ''}`;
};

const emptyLine = (overview: WorkerLifecycleOverview): string => {
  const { stage, suppressed_reason } = overview;
  if (suppressed_reason === 'do_not_contact') {
    return 'Cannot contact (do not contact set).';
  }
  const suppressed = suppressedLabel(overview);
  // graphy still sends a declined worker the shift reminder and the payout
  // notice (Suppression::WORK_MESSAGES, Jira 61259 item 6).
  if (suppressed_reason === 'interview_declined') {
    return `No journeys: ${suppressed}. Shift reminders and payout notices still go.`;
  }
  if (suppressed) return `No messages: ${suppressed}.`;
  // The sweep starts no journey for a paused worker, so "the next sweep" below
  // would be wrong (bug hunt 16).
  if (overview.preferences.journeys_paused_at) {
    return 'No journeys while journeys are paused.';
  }
  if (stage.key === 'awaiting_review') {
    const days = stage.days_in_stage;
    const where = stage.step_name ? `: ${stage.step_name}` : '';
    const age = days === null ? '' : `, ${days} ${days === 1 ? 'day' : 'days'}`;
    return `Waiting on us${where}${age}. Journeys continue after the review.`;
  }
  return 'Next check: the next sweep.';
};

type Props = {
  overview: WorkerLifecycleOverview;
  canEnrol: boolean;
  stepLabel: (key: string) => string;
  onEnrol: () => void;
};

const JourneyCard = ({ overview, canEnrol, stepLabel, onEnrol }: Props) => {
  const active = overview.journeys.find((j) => j.status === 'active');
  const past = overview.journeys.filter((j) => j !== active);
  const stage = stagePill(overview.stage.key, overview.stage.step_name);
  const cannotContact = overview.suppressed_reason === 'do_not_contact';
  const pill = cannotContact
    ? { text: 'Cannot contact', tone: 'red' as const }
    : suppressedLabel(overview)
      ? { text: 'Not messaged', tone: 'red' as const }
      : stage;
  const days = overview.stage.days_in_stage;

  return (
    <Card
      aria-labelledby="journeyCardTitle"
      css={{ flex: '2 1 460px' }}
      data-testid="journeyCard"
    >
      <CardBody css={{ gap: '10px' }}>
        <Row>
          <Title id="journeyCardTitle">Journey</Title>
          <Pill tone={pill.tone}>{pill.text}</Pill>
        </Row>
        {active ? (
          <>
            <Row css={{ gap: '8px 16px' }}>
              <strong style={{ fontSize: 18 }}>
                {active.journey_key} · {active.journey_name}
              </strong>
              {active.day !== null && (
                <Label>{`Day ${active.day} of ${active.window_days}`}</Label>
              )}
              {active.holdout && <Pill tone="grey">Holdout group</Pill>}
            </Row>
            {days !== null && (
              <Label>{`${stagePill(overview.stage.key, overview.stage.step_name).text}, ${days} ${days === 1 ? 'day' : 'days'}`}</Label>
            )}
            <Label
              css={
                overview.preferences.journeys_paused_at
                  ? { color: '#7A5600', fontWeight: 600 }
                  : undefined
              }
            >
              {nextLine(active, overview, stepLabel)}
            </Label>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <strong>Not in a journey.</strong>
            <Label>{emptyLine(overview)}</Label>
          </div>
        )}
        <Divider css={{ paddingTop: '10px', gap: '6px' }}>
          <Label css={{ fontWeight: 600 }}>Past journeys</Label>
          {past.length === 0 && <Caption>None yet.</Caption>}
          {past.map((journey) => (
            <Row key={journey.id} css={{ gap: '6px 12px' }}>
              <span>{`${journey.journey_key} · ${journey.journey_name}`}</span>
              <Caption>
                {`${formatDay(journey.entered_at)}${journey.ended_at ? ` – ${formatDay(journey.ended_at)}` : ''}`}
              </Caption>
              <Pill tone="grey">
                {exitLabel(journey.exit_reason, journey.status)}
              </Pill>
            </Row>
          ))}
        </Divider>
        {canEnrol && (
          <Divider css={{ paddingTop: '10px' }}>
            <Row>
              <Button
                a11yLabel="Enrol in journey"
                data-testid="enrolJourneyBtn"
                label="Enrol in journey"
                onClick={onEnrol}
              />
            </Row>
          </Divider>
        )}
      </CardBody>
    </Card>
  );
};

export default JourneyCard;
